// A course on the Academy home: thumbnail on top, then title, blurb, the
// "N lessons · M min" line and the gate chip. Same border/hover rules as
// ItemCard, but stacked under a thumbnail like a video catalog card.
import cn from "classnames";
import { useNavigate } from "react-router-dom";
import { courseMinutes, courseProgress, lessonsForCourse } from "../catalog";
import { Course } from "../catalog/types";
import { useAcademyProgress } from "../progress";
import { GateChip } from "./Chip";
import CourseThumbnail from "./CourseThumbnail";
import { useGateStatus } from "./gates";

export const coursePath = (c: Course) => `/academy/${c.id}`;

const CourseCard: React.FC<{ course: Course; className?: string }> = ({ course, className }) => {
  const navigate = useNavigate();
  const { progress } = useAcademyProgress();
  const gate = useGateStatus(course.gate);
  const lessons = lessonsForCourse(course.id);
  const p = courseProgress(course.id, progress);
  const first = lessons[0];
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(coursePath(course))}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(coursePath(course));
        }
      }}
      className={cn(
        "group flex flex-col rounded-lg border border-base-300 bg-base-100 overflow-hidden cursor-pointer transition-all",
        "hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
        className,
      )}
    >
      <CourseThumbnail course={course} size="card" progressPct={p.pct} glyph={first?.sceneId ? "watch" : null} className="border-0 border-b rounded-t-lg" />
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="m-0 font-medium text-sm text-base-content group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">{course.title}</h3>
        <p className="m-0 mt-1 text-xs text-base-content/60 line-clamp-2">{course.blurb}</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-base-content/60">
          <span>
            {lessons.length} lesson{lessons.length === 1 ? "" : "s"} · {courseMinutes(course.id)} min
          </span>
          {p.done > 0 && (
            <span className="tabular-nums">
              · {p.done}/{p.total} done
            </span>
          )}
          {gate && <GateChip label={gate.label} locked={gate.locked} reason={gate.reason} />}
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
